import React from 'react'
import Head from 'next/head'
import { InferGetServerSidePropsType } from 'next'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import Layout from '@scentregroup/shared/hub-components/layout'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { GetServerSideProps, GetServerSidePropsContext } from '../types/next'
import { makeApolloClient } from '../lib/apolloClient'
import { getContentPage } from '../lib/get-content-detail/content-page'
import Content from '../components/page-templates/content-detail/content-page'
import { getTopStripProps } from '@scentregroup/shared/hub-components/top-strip'
import { fetchNationalStaticData } from '@scentregroup/shared/helpers/fetch-header-static-data'

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  res,
}: GetServerSidePropsContext) => {
  setCacheHeaders(res)
  const client = makeApolloClient()
  const country = localeToCountry(locale)

  const [nationalHeaderData, privacyPolicyContent, topStripProps] =
    await Promise.all([
      fetchNationalStaticData(client, { country }),
      getContentPage(client, country, 'privacy-policy'),
      getTopStripProps(client, country),
    ])

  if (!privacyPolicyContent) {
    return {
      notFound: true,
    }
  }

  return {
    props: { nationalHeaderData, country, topStripProps, privacyPolicyContent },
  }
}

const PrivacyPolicyPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = ({ nationalHeaderData, country, topStripProps, privacyPolicyContent }) => {
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const metaData = getDefaultNationalMetaData({
    country,
    type: 'privacy-policy',
  })

  return (
    <>
      <Head>
        <link key="canonical" rel="canonical" href={canonical} />
      </Head>
      <Layout
        country={country}
        topStrip={topStripProps}
        header={{
          variant: 'national',
          data: nationalHeaderData,
        }}
        metaData={metaData}
        showRobotsMetaTag={false}
      >
        <Content
          modules={privacyPolicyContent.modules}
          title={privacyPolicyContent.metaData.title}
        />
      </Layout>
    </>
  )
}

export default PrivacyPolicyPage
